import type { L } from '../types/i18n'

export const copy = {
  nav: {
    home: { en: 'Home', es: 'Inicio' },
    services: { en: 'Services', es: 'Servicios' },
    about: { en: 'About', es: 'Acerca de' },
    gallery: { en: 'Gallery', es: 'Galería' },
    reviews: { en: 'Reviews', es: 'Reseñas' },
    faq: { en: 'FAQ', es: 'Preguntas' },
    contact: { en: 'Contact', es: 'Contacto' },
    openMenu: { en: 'Open menu', es: 'Abrir menú' },
    closeMenu: { en: 'Close menu', es: 'Cerrar menú' },
    mainNav: { en: 'Main navigation', es: 'Navegación principal' },
    mobileNav: { en: 'Mobile navigation', es: 'Navegación móvil' },
    language: { en: 'Language', es: 'Idioma' },
  },
  cta: {
    book: { en: 'Book Appointment', es: 'Agendar Cita' },
    bookShort: { en: 'Book', es: 'Agendar' },
    bookNow: { en: 'Book Now', es: 'Agendar' },
    call: { en: 'Call', es: 'Llamar' },
    callNow: { en: 'Call Now', es: 'Llamar Ahora' },
    directions: { en: 'Get Directions', es: 'Cómo Llegar' },
    directionsShort: { en: 'Directions', es: 'Cómo Llegar' },
    text: { en: 'Text Us', es: 'Enviar Texto' },
    whatsapp: { en: 'Text / WhatsApp', es: 'Texto / WhatsApp' },
    learnMore: { en: 'Learn more', es: 'Más información' },
    viewAllServices: { en: 'View all services', es: 'Ver todos los servicios' },
    seeGallery: { en: 'See full gallery', es: 'Ver galería completa' },
    readGoogle: {
      en: 'Read more reviews on Google',
      es: 'Ver más reseñas en Google',
    },
    readAllGoogle: {
      en: 'Read all reviews on Google',
      es: 'Ver todas las reseñas en Google',
    },
    bookThisService: { en: 'Book this service', es: 'Agendar este servicio' },
    askQuestion: { en: 'Ask a question', es: 'Hacer una pregunta' },
  },
  topbar: {
    spanish: { en: 'Se habla español', es: 'Se habla español' },
  },
  hero: {
    eyebrow: {
      en: 'Expert Automotive Repair & Diagnostics · Havre de Grace, MD',
      es: 'Reparación y Diagnóstico Automotriz Experto · Havre de Grace, MD',
    },
    headline1: { en: 'Expert Diagnostics.', es: 'Diagnóstico Experto.' },
    headline2: {
      en: 'Quality Repairs.',
      es: 'Reparaciones de Calidad.',
    },
    headline3: { en: 'Fair Prices.', es: 'Precios Justos.' },
    tagline: {
      en: 'We explain everything clearly, fix it right the first time, and charge a fair price. That’s the A&J difference.',
      es: 'Explicamos todo claramente, arreglamos bien a la primera y cobramos un precio justo. Esa es la diferencia A&J.',
    },
    subheadline: {
      en: 'A full-service auto repair and diagnostic center on Pulaski Highway. Brakes, suspension, maintenance, A/C and timing belts — explained in plain language, priced up front, and finished the same day whenever we can.',
      es: 'Centro completo de reparación y diagnóstico automotriz en la Carretera Pulaski. Frenos, suspensión, mantenimiento, A/C y correas de distribución — explicadas en lenguaje claro, precios por adelantado y terminadas el mismo día cuando es posible.',
    },
    ratingSuffix: { en: 'Google reviews', es: 'reseñas de Google' },
  },
  trust: [
    {
      title: {
        en: '4.8 stars, 25 Google reviews',
        es: '4.8 estrellas, 25 reseñas',
      },
      detail: {
        en: 'Verified customer feedback',
        es: 'Opiniones verificadas de clientes',
      },
    },
    {
      title: { en: 'Expert Team', es: 'Equipo Experto' },
      detail: {
        en: 'All makes, all models',
        es: 'Todas las marcas y modelos',
      },
    },
    {
      title: { en: 'Same-Day Service', es: 'Servicio el Mismo Día' },
      detail: {
        en: 'Most repairs finished today',
        es: 'La mayoría se terminan hoy',
      },
    },
    {
      title: { en: 'Se habla español', es: 'Se habla español' },
      detail: {
        en: 'Bilingual team on site',
        es: 'Equipo bilingüe en el taller',
      },
    },
  ],
  why: {
    title: {
      en: 'Why Drivers in Harford County Keep Coming Back',
      es: 'Por Qué Los Conductores del Condado de Harford Siguen Regresando',
    },
    description: {
      en: 'A&J is a neighborhood shop that runs like a professional service center — the diagnostic capability of a dealership, with someone at the counter who will actually explain it to you.',
      es: 'A&J es un taller del vecindario que funciona como un centro de servicio profesional — la capacidad de diagnóstico de una concesionaria, con alguien en el mostrador que realmente te lo explica.',
    },
    cards: [
      {
        title: {
          en: 'Expert Diagnostic Services',
          es: 'Servicios de Diagnóstico Experto',
        },
        body: {
          en: 'We use state-of-the-art diagnostic equipment to accurately identify vehicle issues. We don’t guess — we diagnose. You know exactly what’s wrong before we start the work.',
          es: 'Utilizamos equipos de diagnóstico de última generación para identificar con precisión los problemas de tu vehículo. No adivinamos — diagnosticamos. Sabes exactamente qué está mal antes de empezar el trabajo.',
        },
        cta: { en: 'How we diagnose', es: 'Cómo diagnosticamos' },
        to: '/services#diagnostics',
      },
      {
        title: {
          en: 'Experienced Professionals',
          es: 'Profesionales Experimentados',
        },
        body: {
          en: 'Our team has years of hands-on experience with all vehicle types. We work quickly, do high-quality repairs, and you can tell we actually care about getting you safely back on the road.',
          es: 'Nuestro equipo tiene años de experiencia práctica con todo tipo de vehículos. Trabajamos rápido, hacemos reparaciones de alta calidad y puedes ver que nos importa ponerte seguro de regreso en la carretera.',
        },
        cta: { en: 'Meet the team', es: 'Conoce el equipo' },
        to: '/about',
      },
      {
        title: { en: 'Transparent Pricing', es: 'Precios Transparentes' },
        body: {
          en: 'No surprises. We explain the problem, show you your options, and give you a fair price before anything gets taken apart. Same-day service often available.',
          es: 'Sin sorpresas. Explicamos el problema, te mostramos tus opciones y te damos un precio justo antes de que se desarme cualquier cosa. Servicio el mismo día frecuentemente disponible.',
        },
        cta: { en: 'View pricing', es: 'Ver precios' },
        to: '/services',
      },
      {
        title: { en: 'Bilingual Service', es: 'Servicio Bilingüe' },
        body: {
          en: 'Communication is the difference between a repair and a good repair. Our team speaks English and Spanish fluently. Your comfort matters.',
          es: 'La comunicación es la diferencia entre una reparación y una buena reparación. Nuestro equipo habla inglés y español con fluidez. Tu comodidad importa.',
        },
        cta: { en: 'Learn more', es: 'Más información' },
        to: '/about',
      },
    ],
  },
  servicesHome: {
    title: { en: 'What We Work On', es: 'En Qué Trabajamos' },
    description: {
      en: 'From a quick oil change to a full timing belt job — and the diagnostic work other shops turn down.',
      es: 'Desde un cambio de aceite rápido hasta una correa de distribución completa — y el diagnóstico que otros talleres rechazan.',
    },
  },
  process: {
    title: {
      en: 'How a Repair Works Here',
      es: 'Cómo Funciona una Reparación Aquí',
    },
    intro: {
      en: 'Three steps, and you know the price before step three begins. No surprises, no pressure to fix it all today.',
      es: 'Tres pasos, y sabes el precio antes de que comience el paso tres. Sin sorpresas, sin presión para arreglarlo todo hoy.',
    },
    stepLabel: { en: 'Step', es: 'Paso' },
    steps: [
      {
        title: { en: 'Schedule Your Appointment', es: 'Agenda tu Cita' },
        body: {
          en: 'Book online, call, or text us. We will get you in quickly — often the same day for diagnostics and routine service.',
          es: 'Reserva en línea, llama o envía un mensaje de texto. Te atenderemos rápidamente — a menudo el mismo día para diagnóstico y servicio rutinario.',
        },
      },
      {
        title: { en: 'Professional Diagnosis', es: 'Diagnóstico Profesional' },
        body: {
          en: 'We inspect your vehicle, verify the cause, and explain exactly what is needed and what it costs. No surprises, no pressure to fix it all today.',
          es: 'Inspeccionamos tu vehículo, verificamos la causa y explicamos exactamente qué se necesita y qué cuesta. Sin sorpresas, sin presión para arreglarlo todo hoy.',
        },
      },
      {
        title: {
          en: 'Expert Repair & Service',
          es: 'Reparación y Servicio Experto',
        },
        body: {
          en: 'We fix it right the first time with quality parts, then walk you through the finished work before you pay. Your vehicle is ready to go safely back on the road.',
          es: 'Lo arreglamos bien a la primera con piezas de calidad, luego te mostramos el trabajo terminado antes de pagar. Tu vehículo está listo para volver seguro a la carretera.',
        },
      },
    ],
  },
  testimonials: {
    title: { en: 'What Our Customers Say', es: 'Lo Que Dicen Nuestros Clientes' },
    description: {
      en: 'Real feedback from drivers across the area. Customers mention the same three things: honest diagnosis, no upselling, and fast turnaround.',
      es: 'Retroalimentación real de conductores del área. Los clientes mencionan las mismas tres cosas: diagnóstico honesto, sin ventas forzadas y entrega rápida.',
    },
    prev: { en: 'Previous review', es: 'Reseña anterior' },
    next: { en: 'Next review', es: 'Reseña siguiente' },
    select: { en: 'Select review', es: 'Seleccionar reseña' },
    reviewFrom: { en: 'Review from', es: 'Reseña de' },
    starsLabel: { en: 'out of 5 stars', es: 'de 5 estrellas' },
  },
  galleryHome: {
    title: { en: 'Inside the Shop', es: 'Dentro del Taller' },
    description: {
      en: 'Real work, real bays. Tap any photo to see it larger.',
      es: 'Trabajo real, bahías reales. Toca cualquier foto para verla más grande.',
    },
    close: { en: 'Close image', es: 'Cerrar imagen' },
  },
  contactHome: {
    title: { en: 'Find Us on Pulaski Highway', es: 'Encuéntranos en la Carretera Pulaski' },
    description: {
      en: 'Easy to reach from Route 40, I-95 and Aberdeen. Drop off before work or wait in the lobby.',
      es: 'Fácil acceso desde la Ruta 40, la I-95 y Aberdeen. Deja tu auto antes del trabajo o espera en la recepción.',
    },
    addressLabel: { en: 'Shop address', es: 'Dirección del taller' },
    phoneLabel: { en: 'Phone', es: 'Teléfono' },
    hoursLabel: { en: 'Hours', es: 'Horario' },
    emailLabel: { en: 'Email', es: 'Correo' },
    mapLabel: {
      en: 'Google map showing A&J Auto Repair at 910 Pulaski Hwy, Havre De Grace, MD',
      es: 'Mapa de Google con A&J Auto Repair en 910 Pulaski Hwy, Havre De Grace, MD',
    },
  },
  ctaBand: {
    title: {
      en: 'Ready to get your vehicle looked at?',
      es: '¿Listo para que vean tu vehículo?',
    },
    description: {
      en: 'Book online in under a minute, or call and talk to a real mechanic. Same-day service is often available.',
      es: 'Reserva en línea en menos de un minuto, o llama y habla con un mecánico real. El servicio del mismo día frecuentemente está disponible.',
    },
  },
  form: {
    title: { en: 'Request an Appointment', es: 'Solicita una Cita' },
    intro: {
      en: 'Tell us about the vehicle and when you would like to come in. We confirm every request by phone — no automated scheduling surprises.',
      es: 'Cuéntanos sobre el vehículo y cuándo te gustaría venir. Confirmamos cada solicitud por teléfono — sin sorpresas de agenda automática.',
    },
    name: { en: 'Full Name', es: 'Nombre Completo' },
    phone: { en: 'Phone Number', es: 'Número de Teléfono' },
    email: { en: 'Email', es: 'Correo Electrónico' },
    optional: { en: '(optional)', es: '(opcional)' },
    vehicle: {
      en: 'Vehicle Year, Make & Model',
      es: 'Año, Marca y Modelo del Vehículo',
    },
    serviceType: { en: 'Service Type', es: 'Tipo de Servicio' },
    selectService: { en: 'Select a service…', es: 'Selecciona un servicio…' },
    other: { en: 'Other / not sure', es: 'Otro / no estoy seguro' },
    otherDetail: { en: 'What do you need?', es: '¿Qué necesitas?' },
    date: { en: 'Preferred Date', es: 'Fecha Preferida' },
    time: { en: 'Preferred Drop-off Time', es: 'Hora Preferida de Entrega' },
    contactMethod: {
      en: 'Preferred Contact Method',
      es: 'Método de Contacto Preferido',
    },
    methodCall: { en: 'Phone call', es: 'Llamada' },
    methodText: { en: 'Text / WhatsApp', es: 'Texto / WhatsApp' },
    methodEmail: { en: 'Email', es: 'Correo electrónico' },
    preferredLanguage: { en: 'Preferred Language', es: 'Idioma Preferido' },
    english: { en: 'English', es: 'Inglés' },
    spanish: { en: 'Español', es: 'Español' },
    message: { en: 'Message', es: 'Mensaje' },
    messagePlaceholder: {
      en: 'Grinding sound from the front right when braking, started last week.',
      es: 'Un rechinido en la llanta delantera derecha al frenar, empezó la semana pasada.',
    },
    submit: { en: 'Request Appointment', es: 'Solicitar Cita' },
    submitting: { en: 'Sending request…', es: 'Enviando solicitud…' },
    errorText: {
      en: 'Something went wrong sending your request. Please call us and we will get you scheduled right away.',
      es: 'Ocurrió un problema al enviar tu solicitud. Por favor llámanos y te agendamos de inmediato.',
    },
    successTitle: { en: 'Request received', es: 'Solicitud recibida' },
    successBody: {
      en: 'Thanks — we have your request. Someone from the shop will contact you to confirm your appointment time, usually within one business hour. Need it sooner?',
      es: 'Gracias — recibimos tu solicitud. Alguien del taller te contactará para confirmar la hora de tu cita, normalmente dentro de una hora laboral. ¿Lo necesitas antes?',
    },
    bookAnother: { en: 'Book another vehicle', es: 'Agendar otro vehículo' },
    helpLine: {
      en: 'Prefer to talk it through? Call',
      es: '¿Prefieres hablarlo? Llama al',
    },
  },
  footer: {
    quickLinks: { en: 'Quick Links', es: 'Enlaces Rápidos' },
    servicesHeading: { en: 'Services', es: 'Servicios' },
    reviewsHeading: {
      en: 'Reviews & Directions',
      es: 'Reseñas y Ubicación',
    },
    googleReviews: { en: 'Google reviews', es: 'reseñas de Google' },
    rights: {
      en: 'All rights reserved.',
      es: 'Todos los derechos reservados.',
    },
    privacy: { en: 'Privacy Policy', es: 'Política de Privacidad' },
    terms: { en: 'Terms of Service', es: 'Términos de Servicio' },
    serviceAreaLabel: { en: 'Service area', es: 'Área de servicio' },
  },
  pages: {
    services: {
      eyebrow: { en: 'Services', es: 'Servicios' },
      title: {
        en: 'Everything we do, and exactly what it includes',
        es: 'Todo lo que hacemos, y exactamente qué incluye',
      },
      description: {
        en: 'One shop for diagnostics and repair on nearly every car, van and light-duty truck on the road. If you are not sure which service you need, call us and describe the symptom — that is usually enough for us to point you the right way.',
        es: 'Un solo taller para diagnóstico y reparación de casi todo auto, van y camioneta ligera. Si no estás seguro de qué servicio necesitas, llámanos y describe el síntoma — con eso normalmente podemos orientarte.',
      },
      jumpLabel: { en: 'Jump to a service', es: 'Ir a un servicio' },
      ctaTitle: {
        en: 'Not sure what your car needs?',
        es: '¿No sabes qué necesita tu auto?',
      },
      ctaDescription: {
        en: 'Describe the noise, the light, or the feel of it. We will tell you what it likely is and what the diagnosis costs before you commit to anything.',
        es: 'Describe el ruido, la luz o cómo se siente. Te diremos qué es probable que sea y cuánto cuesta el diagnóstico antes de que te comprometas a nada.',
      },
      priceNote: {
        en: 'Final price confirmed after diagnosis, before any work begins.',
        es: 'Precio final confirmado después del diagnóstico, antes de empezar el trabajo.',
      },
    },
    about: {
      eyebrow: { en: 'About Us', es: 'Acerca de Nosotros' },
      title: {
        en: 'A neighborhood shop that runs like a diagnostic center',
        es: 'Un taller del vecindario que funciona como centro de diagnóstico',
      },
      description: {
        en: 'A&J Auto Repair LLC was built on a simple idea: most people do not distrust mechanics, they distrust being kept in the dark. So we diagnose properly, show our work, and quote the price before we pick up a wrench.',
        es: 'A&J Auto Repair LLC se construyó sobre una idea simple: la gente no desconfía de los mecánicos, desconfía de que no le expliquen. Por eso diagnosticamos bien, mostramos el trabajo y damos el precio antes de tomar una herramienta.',
      },
      storyTitle: { en: 'Our Story', es: 'Nuestra Historia' },
      story: [
        {
          en: 'We opened our doors on Pulaski Highway to serve drivers in Havre de Grace, Aberdeen and the surrounding Harford County towns — the people who need their car back tomorrow because they need to get to work.',
          es: 'Abrimos nuestras puertas en la Carretera Pulaski para atender a conductores de Havre de Grace, Aberdeen y los pueblos cercanos del Condado de Harford — personas que necesitan su auto mañana porque tienen que ir a trabajar.',
        },
        {
          en: 'We don’t overcomplicate things. You come in, we figure out what’s wrong, we explain it clearly, we fix it right, and you go home knowing exactly what happened and why. That’s it. No surprises. No upselling. Just honest work.',
          es: 'No complicamos las cosas. Vienes, descubrimos qué está mal, lo explicamos claramente, lo arreglamos bien y te vas a casa sabiendo exactamente qué pasó y por qué. Eso es todo. Sin sorpresas. Sin presión de venta. Solo trabajo honesto.',
        },
        {
          en: 'What started as general repair grew into a full diagnostic center, because customers kept arriving with problems other shops could not identify — or would not take on. Some of our best reviews come from exactly those jobs.',
          es: 'Lo que empezó como reparación general creció hasta ser un centro completo de diagnóstico, porque los clientes seguían llegando con problemas que otros talleres no podían identificar — o no querían aceptar. Algunas de nuestras mejores reseñas vienen justo de esos trabajos.',
        },
      ],
      statRating: { en: 'Average Google rating', es: 'Calificación promedio en Google' },
      statReviews: { en: 'Verified customer reviews', es: 'Reseñas verificadas de clientes' },
      statLanguages: {
        en: 'Languages spoken at the counter',
        es: 'Idiomas que hablamos en el mostrador',
      },
      commitmentsTitle: {
        en: 'What you can hold us to',
        es: 'A qué nos puedes exigir',
      },
      commitmentsDescription: {
        en: 'Four commitments that apply to every vehicle that comes through the bay doors.',
        es: 'Cuatro compromisos que aplican a cada vehículo que entra al taller.',
      },
      commitments: [
        {
          title: {
            en: 'Diagnose before we repair',
            es: 'Diagnosticar antes de reparar',
          },
          body: {
            en: 'Every job starts with verification. We would rather spend an extra half hour confirming the cause than sell you a part you did not need.',
            es: 'Cada trabajo empieza con verificación. Preferimos pasar media hora extra confirmando la causa que venderte una pieza que no necesitabas.',
          },
        },
        {
          title: {
            en: 'Price up front, always',
            es: 'Precio por adelantado, siempre',
          },
          body: {
            en: 'You approve the work and the number before we start. If we find something else along the way, we stop and call you.',
            es: 'Tú apruebas el trabajo y el precio antes de empezar. Si encontramos algo más en el camino, nos detenemos y te llamamos.',
          },
        },
        {
          title: {
            en: 'Quality parts and workmanship',
            es: 'Piezas y mano de obra de calidad',
          },
          body: {
            en: 'Quality OE or reputable aftermarket parts, backed by a parts and labor warranty printed on your invoice.',
            es: 'Piezas originales de calidad o alternativas confiables, respaldadas por una garantía de piezas y mano de obra impresa en tu factura.',
          },
        },
        {
          title: {
            en: 'Explained in your language',
            es: 'Explicado en tu idioma',
          },
          body: {
            en: 'English or Spanish, without the jargon. You should leave knowing what was wrong and how we fixed it.',
            es: 'Inglés o español, sin tecnicismos. Debes salir sabiendo qué estaba mal y cómo lo arreglamos.',
          },
        },
      ],
    },
    gallery: {
      eyebrow: { en: 'Gallery', es: 'Galería' },
      title: {
        en: 'See the shop before you drop your car off',
        es: 'Conoce el taller antes de dejar tu auto',
      },
      description: {
        en: 'Our bays, our equipment, and the kind of work that leaves here every day. Select any photo to open it full size.',
        es: 'Nuestras bahías, nuestro equipo y el tipo de trabajo que sale de aquí todos los días. Selecciona cualquier foto para verla en tamaño completo.',
      },
      ctaTitle: {
        en: 'Want to see your repair in progress?',
        es: '¿Quieres ver tu reparación en proceso?',
      },
      ctaDescription: {
        en: 'Ask us for photos when you drop off. We are happy to show you the worn part next to the new one.',
        es: 'Pídenos fotos cuando dejes tu auto. Con gusto te mostramos la pieza desgastada junto a la nueva.',
      },
    },
    reviews: {
      eyebrow: { en: 'Reviews', es: 'Reseñas' },
      title: {
        en: 'What our customers actually say',
        es: 'Lo que realmente dicen nuestros clientes',
      },
      description: {
        en: 'Every review below is a real Google review from a customer of our shop.',
        es: 'Cada reseña aquí abajo es una reseña real de Google de un cliente de nuestro taller.',
      },
      translatedNote: {
        en: 'Reviews were originally written in English.',
        es: 'Las reseñas se escribieron originalmente en inglés y se muestran traducidas.',
      },
      ctaTitle: {
        en: 'Been in before? Leave us a review.',
        es: '¿Ya nos visitaste? Déjanos una reseña.',
      },
      ctaDescription: {
        en: 'Reviews are how neighbors find a shop they can trust. If we did right by you, a few words on Google go a long way.',
        es: 'Las reseñas son como los vecinos encuentran un taller de confianza. Si te atendimos bien, unas palabras en Google ayudan mucho.',
      },
    },
    faq: {
      eyebrow: { en: 'FAQ', es: 'Preguntas Frecuentes' },
      title: {
        en: 'Questions we get at the counter',
        es: 'Preguntas que nos hacen en el mostrador',
      },
      description: {
        en: 'Appointments, pricing, warranty and the maintenance questions worth knowing. If yours is not here, call and ask — we would rather answer it than have you guess.',
        es: 'Citas, precios, garantía y las preguntas de mantenimiento que vale la pena conocer. Si la tuya no está aquí, llama y pregunta — preferimos responderte que dejarte adivinando.',
      },
      ctaTitle: { en: 'Still have a question?', es: '¿Aún tienes una pregunta?' },
      ctaDescription: {
        en: 'Call the shop and talk to the person who will be working on your car. No phone tree, no call center.',
        es: 'Llama al taller y habla con la persona que trabajará en tu auto. Sin menús telefónicos, sin call center.',
      },
    },
    contact: {
      eyebrow: { en: 'Contact & Booking', es: 'Contacto y Citas' },
      title: { en: 'Book your appointment', es: 'Agenda tu cita' },
      description: {
        en: 'Send a request and we will contact you to confirm a time, usually within one business hour. If your car is not safe to drive, call us instead so we can advise you right away.',
        es: 'Envía una solicitud y te contactamos para confirmar la hora, normalmente dentro de una hora laboral. Si tu auto no es seguro para conducir, mejor llámanos para asesorarte de inmediato.',
      },
      visitTitle: { en: 'Visit the shop', es: 'Visita el taller' },
      visitDescription: {
        en: '910 Pulaski Highway, minutes from Route 40 and I-95. Mon–Sat, 8:00 AM to 5:00 PM.',
        es: '910 Pulaski Highway, a minutos de la Ruta 40 y la I-95. Lun–Sáb, 8:00 AM a 5:00 PM.',
      },
    },
  },
  seo: {
    home: {
      title: {
        en: 'A&J Auto Repair | Professional Car Repair Havre de Grace, MD',
        es: 'A&J Auto Repair | Taller Mecánico en Havre de Grace, MD',
      },
      description: {
        en: 'Professional auto repair in Havre de Grace, MD. Expert diagnostics, brake service, suspension repair, A/C service. Fair pricing. Bilingual team. Call (410) 942-5091.',
        es: 'Reparación automotriz profesional en Havre de Grace, MD. Diagnóstico experto, frenos, suspensión y servicio de A/C. Precios justos. Equipo bilingüe. Llama al (410) 942-5091.',
      },
    },
    services: {
      title: {
        en: 'Auto Repair Services | A&J Auto Repair Havre de Grace, MD',
        es: 'Servicios de Reparación | A&J Auto Repair Havre de Grace, MD',
      },
      description: {
        en: 'Diagnostics, brake service, suspension and struts, tune-ups, oil changes, timing belts, A/C service and general repair in Havre de Grace, Maryland. Call (410) 942-5091.',
        es: 'Diagnóstico, frenos, suspensión y amortiguadores, afinaciones, cambios de aceite, correas de distribución, A/C y reparación general en Havre de Grace, Maryland. Llama al (410) 942-5091.',
      },
    },
    about: {
      title: {
        en: 'About A&J Auto Repair | Expert Mechanics in Havre de Grace, MD',
        es: 'Acerca de A&J Auto Repair | Mecánicos Expertos en Havre de Grace, MD',
      },
      description: {
        en: 'Meet A&J Auto Repair LLC — a bilingual diagnostic and repair center on Pulaski Highway in Havre de Grace, Maryland. 4.8 stars across 25 Google reviews.',
        es: 'Conoce A&J Auto Repair LLC — centro bilingüe de diagnóstico y reparación en la Carretera Pulaski en Havre de Grace, Maryland. 4.8 estrellas en 25 reseñas de Google.',
      },
    },
    gallery: {
      title: {
        en: 'Shop Gallery | A&J Auto Repair Havre de Grace, MD',
        es: 'Galería del Taller | A&J Auto Repair Havre de Grace, MD',
      },
      description: {
        en: 'See inside A&J Auto Repair in Havre de Grace, MD — our service bays, diagnostic equipment, brake and suspension work, and the team behind the repairs.',
        es: 'Mira el interior de A&J Auto Repair en Havre de Grace, MD — nuestras bahías, equipo de diagnóstico, trabajos de frenos y suspensión, y el equipo detrás de las reparaciones.',
      },
    },
    reviews: {
      title: {
        en: 'Customer Reviews | A&J Auto Repair Havre de Grace, MD',
        es: 'Reseñas de Clientes | A&J Auto Repair Havre de Grace, MD',
      },
      description: {
        en: 'Read real Google reviews for A&J Auto Repair LLC in Havre de Grace, Maryland — 4.8 stars from 25 customers who mention honest diagnosis and fair pricing.',
        es: 'Lee reseñas reales de Google de A&J Auto Repair LLC en Havre de Grace, Maryland — 4.8 estrellas de 25 clientes que destacan el diagnóstico honesto y los precios justos.',
      },
    },
    faq: {
      title: {
        en: 'Auto Repair FAQ | A&J Auto Repair Havre de Grace, MD',
        es: 'Preguntas Frecuentes | A&J Auto Repair Havre de Grace, MD',
      },
      description: {
        en: 'Answers about appointments, diagnostics, warranty, payment methods and vehicle care from A&J Auto Repair in Havre de Grace, Maryland.',
        es: 'Respuestas sobre citas, diagnóstico, garantía, formas de pago y cuidado del vehículo de A&J Auto Repair en Havre de Grace, Maryland.',
      },
    },
    contact: {
      title: {
        en: 'Book an Appointment | A&J Auto Repair Havre de Grace, MD',
        es: 'Agenda una Cita | A&J Auto Repair Havre de Grace, MD',
      },
      description: {
        en: 'Book auto repair in Havre de Grace, MD. Request an appointment online or call (410) 942-5091. 910 Pulaski Hwy, open Mon–Sat 8am–5pm. Se habla español.',
        es: 'Agenda tu reparación en Havre de Grace, MD. Solicita una cita en línea o llama al (410) 942-5091. 910 Pulaski Hwy, abierto Lun–Sáb 8am–5pm. Se habla español.',
      },
    },
  },
} satisfies Record<string, unknown>

export type Copy = typeof copy
export type { L }
